import { supabase } from "@/integrations/supabase/client";
import { formatDateLocal } from "@/lib/timeFormatUtils";

export interface ReservationImpact {
  hasFutureReservations: boolean;
  futureCount: number;
  pastCount: number;
  futureReservations: Array<{
    id: string;
    date: string;
    startTime: string;
    numberOfPeople: number;
    customerName: string;
  }>;
}

/**
 * Vérifie l'impact sur les réservations pour une table simple
 */
export async function checkTableReservationImpact(tableId: string): Promise<ReservationImpact> {
  const today = formatDateLocal(new Date());

  // Récupérer les réservations futures
  const { data: futureReservations, error: futureError } = await supabase
    .from('reservations')
    .select(`
      id,
      date,
      start_time,
      number_of_people,
      customer_id,
      customers (
        first_name,
        last_name
      )
    `)
    .eq('table_id', tableId)
    .gte('date', today)
    .neq('status', 'annulé')
    .order('date', { ascending: true })
    .order('start_time', { ascending: true });

  if (futureError) {
    console.error('Erreur lors de la vérification des réservations futures:', futureError);
    throw futureError;
  }

  // Récupérer le nombre de réservations passées
  const { count: pastCount, error: pastError } = await supabase
    .from('reservations')
    .select('id', { count: 'exact', head: true })
    .eq('table_id', tableId)
    .lt('date', today);

  if (pastError) {
    console.error('Erreur lors du comptage des réservations passées:', pastError);
  }

  const formattedReservations = (futureReservations || []).map(res => ({
    id: res.id,
    date: res.date,
    startTime: res.start_time,
    numberOfPeople: res.number_of_people,
    customerName: res.customers 
      ? `${res.customers.first_name} ${res.customers.last_name}`
      : 'Client inconnu'
  }));

  return {
    hasFutureReservations: formattedReservations.length > 0,
    futureCount: formattedReservations.length,
    pastCount: pastCount || 0,
    futureReservations: formattedReservations
  };
}

/**
 * Vérifie l'impact sur les réservations pour une table combinée
 */
export async function checkCombinedTableReservationImpact(combinedTableId: string): Promise<ReservationImpact> {
  const today = formatDateLocal(new Date());

  // Récupérer les réservations futures
  const { data: futureReservations, error: futureError } = await supabase
    .from('reservations')
    .select(`
      id,
      date,
      start_time,
      number_of_people,
      customer_id,
      customers (
        first_name,
        last_name
      )
    `)
    .eq('combined_table_id', combinedTableId)
    .gte('date', today)
    .neq('status', 'annulé')
    .order('date', { ascending: true })
    .order('start_time', { ascending: true });

  if (futureError) {
    console.error('Erreur lors de la vérification des réservations futures:', futureError);
    throw futureError;
  }

  // Récupérer le nombre de réservations passées
  const { count: pastCount, error: pastError } = await supabase
    .from('reservations')
    .select('id', { count: 'exact', head: true })
    .eq('combined_table_id', combinedTableId)
    .lt('date', today);

  if (pastError) {
    console.error('Erreur lors du comptage des réservations passées:', pastError);
  }

  const formattedReservations = (futureReservations || []).map(res => ({
    id: res.id,
    date: res.date,
    startTime: res.start_time,
    numberOfPeople: res.number_of_people,
    customerName: res.customers 
      ? `${res.customers.first_name} ${res.customers.last_name}`
      : 'Client inconnu'
  }));

  return {
    hasFutureReservations: formattedReservations.length > 0,
    futureCount: formattedReservations.length,
    pastCount: pastCount || 0,
    futureReservations: formattedReservations
  };
}

/**
 * Vérifie si une table fait partie de combinaisons actives
 */
export async function checkTableInCombinations(tableId: string) {
  const { data: links, error } = await supabase
    .from('combined_table_tables')
    .select(`
      combined_table_id,
      combined_tables (
        id,
        name,
        is_active
      )
    `)
    .eq('table_id', tableId);

  if (error) {
    console.error('Erreur lors de la vérification des combinaisons:', error);
    throw error;
  }

  const activeCombinations = (links || [])
    .filter(link => link.combined_tables && link.combined_tables.is_active)
    .map(link => ({
      id: link.combined_tables.id,
      name: link.combined_tables.name
    }));

  return {
    isInCombination: activeCombinations.length > 0,
    combinations: activeCombinations
  };
}

/**
 * Vérifie les réservations qui dépassent une nouvelle capacité
 */
export async function checkCapacityExceeded(
  tableId: string,
  newCapacity: number,
  isCombinedTable: boolean = false
): Promise<{
  hasExceeded: boolean;
  exceededReservations: Array<{
    id: string;
    date: string;
    startTime: string;
    numberOfPeople: number;
    customerName: string;
  }>;
}> {
  const today = formatDateLocal(new Date());
  const field = isCombinedTable ? 'combined_table_id' : 'table_id';

  const { data: reservations, error } = await supabase
    .from('reservations')
    .select(`
      id,
      date,
      start_time,
      number_of_people,
      customer_id,
      customers (
        first_name,
        last_name
      )
    `)
    .eq(field, tableId)
    .gte('date', today)
    .neq('status', 'annulé')
    .gt('number_of_people', newCapacity)
    .order('date', { ascending: true })
    .order('start_time', { ascending: true });

  if (error) {
    console.error('Erreur lors de la vérification des capacités:', error);
    throw error;
  }

  const exceededReservations = (reservations || []).map(res => ({
    id: res.id,
    date: res.date,
    startTime: res.start_time,
    numberOfPeople: res.number_of_people,
    customerName: res.customers 
      ? `${res.customers.first_name} ${res.customers.last_name}`
      : 'Client inconnu'
  }));

  return {
    hasExceeded: exceededReservations.length > 0,
    exceededReservations
  };
}

/**
 * Vérifie si une réservation a une table active assignée
 * Retourne true si la réservation n'a pas de table active (problème)
 */
export function hasInactiveOrMissingTable(
  reservation: {
    tableId?: string;
    combinedTableId?: string;
    status: string;
    reservationDate: Date;
  },
  allTables: Array<{ id: string; isActive: boolean }>,
  allCombinedTables: Array<{ id: string; isActive: boolean }>
): boolean {
  // Ignorer les réservations annulées ou en attente
  if (reservation.status === "annulé" || reservation.status === "en_attente") {
    return false;
  }

  // Ignorer les réservations passées
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resDate = new Date(reservation.reservationDate);
  resDate.setHours(0, 0, 0, 0);
  if (resDate < today) {
    return false;
  }

  // Cas 1 : Réservation sur table simple
  if (reservation.tableId) {
    const table = allTables.find(t => t.id === reservation.tableId);
    // Problème si table n'existe pas OU si elle est désactivée
    if (!table || !table.isActive) {
      return true;
    }
  }

  // Cas 2 : Réservation sur table combinée
  if (reservation.combinedTableId) {
    const combinedTable = allCombinedTables.find(ct => ct.id === reservation.combinedTableId);
    // Problème si table combinée n'existe pas OU si elle est désactivée
    if (!combinedTable || !combinedTable.isActive) {
      return true;
    }
  }

  // Cas 3 : Réservation sans aucune table assignée (rare mais possible)
  if (!reservation.tableId && !reservation.combinedTableId) {
    return true;
  }

  return false;
}

/**
 * Vérifie si une réservation dépasse la capacité de sa table assignée
 * Retourne true si le nombre de personnes > capacité de la table
 */
export function hasCapacityExceeded(
  reservation: {
    tableId?: string;
    combinedTableId?: string;
    persons: number;
    status: string;
    reservationDate: Date;
  },
  allTables: Array<{ id: string; capacity: number; isActive: boolean }>,
  allCombinedTables: Array<{ id: string; totalCapacity: number; isActive: boolean }>
): boolean {
  // Ignorer les réservations annulées ou en attente
  if (reservation.status === "annulé" || reservation.status === "en_attente") {
    return false;
  }

  // Ignorer les réservations passées
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resDate = new Date(reservation.reservationDate);
  resDate.setHours(0, 0, 0, 0);
  if (resDate < today) {
    return false;
  }

  // Cas 1 : Réservation sur table simple
  if (reservation.tableId) {
    const table = allTables.find(t => t.id === reservation.tableId);
    // Table existe et est active
    if (table && table.isActive) {
      // Vérifier si le nombre de personnes dépasse la capacité
      return reservation.persons > table.capacity;
    }
  }

  // Cas 2 : Réservation sur table combinée
  if (reservation.combinedTableId) {
    const combinedTable = allCombinedTables.find(ct => ct.id === reservation.combinedTableId);
    // Table combinée existe et est active
    if (combinedTable && combinedTable.isActive) {
      // Vérifier si le nombre de personnes dépasse la capacité totale
      return reservation.persons > combinedTable.totalCapacity;
    }
  }

  return false;
}

/**
 * Vérifie si une réservation tombe pendant une période de fermeture
 * Retourne true si la date de réservation est dans une période de fermeture
 */
export function hasClosureConflict(
  reservation: {
    reservationDate: Date;
    status: string;
  },
  closures: Array<{ start_date: string; end_date: string }>
): boolean {
  // Ignorer les réservations annulées ou en attente
  if (reservation.status === "annulé" || reservation.status === "en_attente") {
    return false;
  }

  // Ignorer les réservations passées
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resDate = new Date(reservation.reservationDate);
  resDate.setHours(0, 0, 0, 0);
  if (resDate < today) {
    return false;
  }

  // Vérifier si la date est dans une période de fermeture
  const dateStr = formatDateLocal(resDate);
  return closures.some(
    closure => dateStr >= closure.start_date && dateStr <= closure.end_date
  );
}
